import { useTranslation } from 'react-i18next'
import { useCogfy, usePermissions, useToasts } from '../../../../hooks'
import { useMutation } from '@tanstack/react-query'
import { CreateCollectionCommand } from '@indigohive/cogfy-types/endpoints/createCollection'
import { CreateFolderCommand } from '@indigohive/cogfy-types/endpoints/createFolder'
import { useState } from 'react'
import { Button } from '../../../Button'
import { PlusIcon } from 'lucide-react'
import { OverlayContent } from '../../../OverlayContent'
import { DrawerCreateCollectionMenu } from '../DrawerCreateCollectionMenu'
import { useNavigate } from 'react-router-dom'

type DrawerCollectionsTreeProps = {
  workspace: string
  onCollectionsChange?: () => void
}

export function DrawerCollectionsTree (props: DrawerCollectionsTreeProps) {
  const { workspace } = props
  const [createCollectionMenuEl, setCreateCollectionMenuEl] = useState<HTMLElement | null>(null)
  const { t } = useTranslation()
  const toasts = useToasts()
  const cogfy = useCogfy()
  const navigate = useNavigate()
  const permissions = usePermissions()
  const canCreateCollection = permissions.isAdmin

  const createFolder = useMutation({
    mutationFn: (data: CreateFolderCommand) => cogfy.createFolder(data),
    onSuccess: (result) => {
      props.onCollectionsChange?.()
      navigate(`/${workspace}/${result.id}`)
    }
  })

  const createCollection = useMutation({
    mutationFn: (data: CreateCollectionCommand) => cogfy.createCollection(data),
    onSuccess: result => {
      navigate(`/${workspace}/${result.id}`)
      toasts.success(t('collectionTemplatesPage:Collection created'))
    },
    onError: () => {
      toasts.error(t('collectionTemplatesPage:Error creating collection'))
    }
  })

  return (
    <div className="flex items-center selection:mb-[-8px]">
      <span className="grow font-semibold">
        {t('Collections')}
      </span>
      {canCreateCollection && (
        <div>
          <Button
            size="sm"
            color="primary"
            onClick={event => setCreateCollectionMenuEl(event.currentTarget)}
          >
            <PlusIcon size={20} />
            {t('Create')}
          </Button>
          <OverlayContent
            open={Boolean(createCollectionMenuEl)}
            anchorEl={createCollectionMenuEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClose={() => setCreateCollectionMenuEl(null)}
          >
            <DrawerCreateCollectionMenu
              workspace={workspace}
              onCreateCollectionClick={() => {
                setCreateCollectionMenuEl(null)
                createCollection.mutate({})
              }}
              onCreateFolderClick={() => {
                setCreateCollectionMenuEl(null)
                createFolder.mutate({ parentId: null })
              }}
              onOpenTemplateGalleryClick={() => setCreateCollectionMenuEl(null)}
            />
          </OverlayContent>
        </div>
      )}
    </div>
  )
}
